import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { useMarketHomeFilters } from '@sorare/marketplace/src/hooks/useMarketHomeFilters';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import { AdvancedCardSearch } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch';

import { CardsShowWithHits } from '../CardsShowWithHits';
import { ClubFilter } from '../ClubFilter';
import type { FeaturedMainLeagueLaunchCardsSearch_competition } from './__generated__/index.graphql';

type Object = object | undefined;
type Competition = FeaturedMainLeagueLaunchCardsSearch_competition | undefined;
type Props = {
  league: LeagueLaunch<Competition, Object>;
};

const sort = 'Cards On Sale Bids Count';

const Title = styled(Horizontal)`
  justify-content: space-between;
`;

export const CardsSearch = ({ league }: Props) => {
  const { sport } = useSportContext();
  const { marketHomeFilters } = useMarketHomeFilters();
  const { rarity } = marketHomeFilters;

  const props = (() => {
    if (sport === Sport.NBA) {
      return {
        extraFilters: league.extraFilters,
        rarity,
        primary: true,
      };
    }
    return {
      leagueSlug: league.competition?.slug || league.slug,
      rarity,
      primary: true,
    };
  })();

  const filters = useDefaultFilters(props);

  return (
    <AdvancedCardSearch
      sport={league.sport}
      sorts={[sort]}
      defaultSort={sort}
      defaultFilters={filters}
      cardFilters={[]}
      GridComponent={CardsShowWithHits}
      removeFinishedAuctions
      removeFinishedPrimaryBuys
      analyticsTags={['NewSeasonLaunch']}
      minimalistic
      distinct
    >
      <Vertical gap={2}>
        <ClubFilter
          leagueSlug={league.competition?.slug || league.slug}
          clubs={league?.clubs}
        />
        <Title>
          <BrandM>
            <FormattedMessage {...navLabels.trending} />
          </BrandM>
        </Title>
      </Vertical>
    </AdvancedCardSearch>
  );
};

CardsSearch.fragments = {
  competition: gql`
    fragment FeaturedMainLeagueLaunchCardsSearch_competition on Competition {
      slug
    }
  ` as TypedDocumentNode<FeaturedMainLeagueLaunchCardsSearch_competition>,
};
